// Phase DP — pure helpers for the document preview modal's type
// dispatch + per-upload status label. Extracted so the dispatch logic
// is unit-testable without rendering a full modal.

const PDF_MIMES = new Set(["application/pdf"]);
const IMAGE_MIMES = new Set([
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/webp",
]);

const PDF_EXTS = new Set([".pdf"]);
const IMAGE_EXTS = new Set([".jpg", ".jpeg", ".png", ".webp"]);

function extOf(filename) {
    if (typeof filename !== "string") return "";
    const m = filename.match(/(\.[a-zA-Z0-9]+)$/);
    return m ? m[1].toLowerCase() : "";
}

/**
 * Classify a file payload (as it comes off the submission's uploads[]
 * array — `{filename, mime_type}`) into one of three preview kinds:
 *   - "pdf"   → render with PdfPreview + toolbar
 *   - "image" → render as a fitted <img>
 *   - "unsupported" → fallback "Preview not supported" message
 *
 * Tolerates missing mime (older upload rows) by falling back to the
 * filename extension. If both are missing/blank → "unsupported" so the
 * modal never crashes.
 */
export function classifyPreviewKind(file) {
    if (!file || typeof file !== "object") return "unsupported";
    const mime = (file.mime_type || file.mime || "").toLowerCase().trim();
    if (PDF_MIMES.has(mime)) return "pdf";
    if (IMAGE_MIMES.has(mime)) return "image";
    const ext = extOf(file.filename || file.name || "");
    if (PDF_EXTS.has(ext)) return "pdf";
    if (IMAGE_EXTS.has(ext)) return "image";
    return "unsupported";
}

/** Per-upload status pill copy / palette. */
export const UPLOAD_STATUS_LABEL = {
    pending: "Pending",
    approved: "Approved",
    rejected: "Rejected",
};

export const UPLOAD_STATUS_CLASS = {
    pending: "bg-slate-100 text-slate-700 ring-slate-200",
    approved: "bg-emerald-50 text-emerald-800 ring-emerald-200",
    rejected: "bg-red-50 text-red-700 ring-red-200",
};

/** Normalize an upload's status field — legacy rows had no status. */
export function normalizeUploadStatus(status) {
    if (status === "approved" || status === "rejected") return status;
    return "pending";
}

// Phase DP — PDF viewer zoom/page clamps. Pure functions kept in this
// lib (not in PdfPreview.jsx) so the test suite can import them without
// pulling react-pdf's CSS imports through Jest's transformer.
export const PDF_ZOOM_MIN = 0.5;
export const PDF_ZOOM_MAX = 3;
export const PDF_ZOOM_STEP = 0.25;

export function clampZoom(value) {
    if (!Number.isFinite(value)) return 1;
    return Math.min(PDF_ZOOM_MAX, Math.max(PDF_ZOOM_MIN, value));
}

export function clampPage(value, total) {
    if (!Number.isFinite(value)) return 1;
    if (!Number.isFinite(total) || total < 1) return 1;
    return Math.min(total, Math.max(1, Math.floor(value)));
}
